/**
 * The browser's half of the feature-request board: a browse hook, a vote toggle
 * and one write.
 *
 * No client library and no query cache, for the same reason `gallery/client.ts`
 * has neither — the surface is three same-origin JSON calls, and the two that
 * need a session carry the cookie the browser sends for free.
 *
 * ## One audience, two positions in it
 *
 * {@link useBoard} is the *visitor's* view and it has to work with no account at
 * all: no session, no workspace, no store. What an account changes is one thing,
 * and it is the same thing it changes on a gallery card — what pressing the
 * control does. A guest sees every count and gets a sentence; a brewer's press
 * reaches the server. Nothing is hidden from anybody.
 *
 * {@link useOwnRequests} is the second hook and the one exception to that: it is
 * about the reader rather than about the list, so it asks for nothing at all
 * without an account and returns an empty list rather than an error when there
 * is no session to answer it. It is a *separate* hook rather than a field on the
 * board's state because the two are different queries with different lifetimes —
 * the board re-runs on every keystroke in the search box, and somebody's own
 * requests do not change when they type.
 *
 * ## The optimistic vote, and what it borrows against
 *
 * {@link BoardState.toggleVote} guesses ±1 so the control answers the finger,
 * then overwrites the guess with the number the server read back out of D1 —
 * never with arithmetic of its own — and puts back exactly what was there before
 * if the write is refused. The page is never re-fetched for a vote: a vote that
 * reshuffled a top-sorted list under somebody's finger would be a strange thing
 * to do to a person who pressed one button.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  BoardCounts,
  BoardCountsResponse,
  BoardPageResponse,
  BoardSort,
  FeatureRequestCard,
  FeatureRequestResponse,
  FeatureRequestStatus,
  OwnRequest,
  OwnRequestsResponse,
  RequestVoteResponse,
} from './protocol';
import {
  boardBrowsePath,
  boardCountsPath,
  REQUESTS_BASE_PATH,
  requestUnvotePath,
  requestVotePath,
} from './protocol';

const JSON_HEADERS = { accept: 'application/json' } as const;
const JSON_WRITE_HEADERS = { ...JSON_HEADERS, 'content-type': 'application/json' } as const;

/** One page of the board, as the hook holds it. */
interface BoardPage {
  readonly requests: readonly FeatureRequestCard[];
  readonly cursor: string | null;
}

/** One page of the public board. `null` covers every kind of "no". */
export async function fetchBoardPage(query: {
  readonly q?: string;
  readonly sort?: BoardSort;
  readonly cursor?: string | null;
  readonly status?: FeatureRequestStatus | null;
}): Promise<BoardPage | null> {
  try {
    const response = await fetch(boardBrowsePath(query), { headers: JSON_HEADERS });
    if (!response.ok) return null;
    const body = (await response.json()) as Partial<BoardPageResponse>;
    return { requests: body.requests ?? [], cursor: body.cursor ?? null };
  } catch {
    return null;
  }
}

/** Why a vote was refused. Stable codes; the row owns the wording. */
export type VoteFailure = 'unauthorized' | 'not-found' | 'unavailable';

/** What a vote or an unvote answers: the server's state, or why not. */
export type VoteResult =
  | { readonly ok: true; readonly voteCount: number; readonly voted: boolean }
  | { readonly ok: false; readonly reason: VoteFailure };

/**
 * Vote for a request, or take it back. Both answer with the state that is now
 * true.
 *
 * One function for both directions, because they are one route apart and one
 * decision in the caller. Nothing here computes a count: `voteCount` is read out
 * of D1 by the same transaction that wrote the row (`worker/board.ts`), which is
 * what lets an optimistic row correct itself without refetching the page it is
 * sitting on.
 */
export async function setRequestVote(requestId: string, voted: boolean): Promise<VoteResult> {
  try {
    const response = await fetch(
      voted ? requestVotePath(requestId) : requestUnvotePath(requestId),
      { method: 'POST', headers: JSON_HEADERS },
    );
    if (response.status === 401) return { ok: false, reason: 'unauthorized' };
    if (response.status === 404) return { ok: false, reason: 'not-found' };
    if (!response.ok) return { ok: false, reason: 'unavailable' };
    const body = (await response.json()) as Partial<RequestVoteResponse>;
    // A 200 with a body this build cannot read is a failure like any other: the
    // row must never render a number it made up.
    if (typeof body.voteCount !== 'number' || typeof body.voted !== 'boolean') {
      return { ok: false, reason: 'unavailable' };
    }
    return { ok: true, voteCount: body.voteCount, voted: body.voted };
  } catch {
    return { ok: false, reason: 'unavailable' };
  }
}

/** Why a request was refused. Stable codes; the form owns the sentences. */
export type RequestFailure = 'invalid' | 'too-many-open' | 'rate-limited' | 'unavailable';

/** What posting a request answers. */
export type RequestResult =
  | { readonly ok: true; readonly request: FeatureRequestCard }
  | { readonly ok: false; readonly reason: RequestFailure };

/**
 * Ask for something.
 *
 * The three refusals are three different sentences and the codes are what keeps
 * them apart: "that will not fit" is the brewer's to fix now, "you have ten open
 * already" is a fact about their account, and "five in an hour" is a fact about
 * the last hour. A single "something went wrong" would leave all three people
 * pressing the button again.
 */
export async function submitFeatureRequest(title: string, body: string): Promise<RequestResult> {
  try {
    const response = await fetch(REQUESTS_BASE_PATH, {
      method: 'POST',
      headers: JSON_WRITE_HEADERS,
      body: JSON.stringify({ title, body }),
    });
    if (!response.ok) return { ok: false, reason: await failureFor(response) };
    const payload = (await response.json()) as Partial<FeatureRequestResponse>;
    if (payload.request === undefined) return { ok: false, reason: 'unavailable' };
    return { ok: true, request: payload.request };
  } catch {
    return { ok: false, reason: 'unavailable' };
  }
}

/** Map a refusal onto {@link RequestFailure}. The body's code decides. */
async function failureFor(response: Response): Promise<RequestFailure> {
  const code = await response
    .json()
    .then((body) => (body as { error?: unknown }).error)
    .catch(() => undefined);
  if (code === 'too_many_open_requests') return 'too-many-open';
  if (code === 'rate_limited') return 'rate-limited';
  if (code === 'bad_request') return 'invalid';
  // A 401 is a page that was open when a session ended. There is no separate
  // sentence for it, because the form is only rendered for somebody signed in.
  return 'unavailable';
}

/** What {@link useBoard} returns. */
export interface BoardState {
  readonly requests: readonly FeatureRequestCard[];
  /** True until the first page of the current query lands. */
  readonly loading: boolean;
  /** True while a further page is being appended. */
  readonly loadingMore: boolean;
  /** True when the last request failed. The list keeps whatever it had. */
  readonly failed: boolean;
  /** Whether there is another page to ask for. */
  readonly hasMore: boolean;
  /** Ask for it. Appends; never replaces. */
  readonly loadMore: () => void;
  /**
   * Re-read the first page: what a successful post calls, and what Try again
   * presses when a read did not reach the server.
   *
   * One name for one act, and the same callback the effect takes (D-LD5). It is
   * `refresh` rather than the gallery's `retry` for the reason the rule note in
   * `gallery/client.ts` gives: this hook had a refresh before the rule did, and
   * two names for one function would only invite them to drift.
   */
  readonly refresh: () => void;
  /**
   * Turn this request's vote on or off.
   *
   * Optimistic and then reconciled: the row moves immediately, the server's
   * `{voteCount, voted}` replaces the guess when it lands, and a refusal puts
   * back exactly what was there before.
   */
  readonly toggleVote: (requestId: string) => Promise<VoteResult>;
}

/**
 * The board, paged.
 *
 * Changing the search or the sort starts a new query and **replaces** the list;
 * {@link BoardState.loadMore} appends to it. Those are two different writes to
 * the same array and keeping them apart is what stops a slow first page landing
 * on top of a second one.
 *
 * A generation counter rather than a `cancelled` flag, the same mechanism
 * `useGallery` and `useAdminData` use and for the same reason: typing in the
 * search box starts a request per keystroke-batch and they can finish out of
 * order, so the question is "is this still the answer to the question being
 * asked" rather than "was this component unmounted".
 */
export function useBoard(query: {
  readonly q: string;
  readonly sort: BoardSort;
  /** Which section this is. `null` is the whole board, as it was before sections. */
  readonly status?: FeatureRequestStatus | null;
  /**
   * Whether to read at all.
   *
   * `false` holds the hook at its empty state and sends nothing, which is what a
   * collapsed section wants: "Shipped (12)" is printed from the counts, and the
   * twelve rows are worth a request only once somebody opens it. It is a
   * parameter rather than a conditional `useBoard` call because a hook cannot be
   * called conditionally, and a section that mounted on expand would lose its
   * page every time it was folded away.
   */
  readonly enabled?: boolean;
}): BoardState {
  const { q, sort, status = null, enabled = true } = query;
  const [requests, setRequests] = useState<readonly FeatureRequestCard[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const generation = useRef(0);

  /**
   * Read the first page of the current query, replacing whatever is on screen.
   *
   * A named callback rather than the body of the effect, because `refresh` is
   * the same act performed on purpose: a re-read after posting, and the one the
   * Try again beside a stale list presses, have to take the exact path a first
   * read takes or the three can drift (D-LD5).
   */
  const run = useCallback(() => {
    generation.current += 1;
    const mine = generation.current;
    // A disabled section is not loading and has not failed; it simply has not
    // been asked. Anything else would render a spinner nobody opened.
    if (!enabled) {
      setRequests([]);
      setCursor(null);
      setFailed(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setFailed(false);
    void fetchBoardPage({ q, sort, status }).then((page) => {
      if (generation.current !== mine) return;
      // A failure changes nothing but the label on it: `requests` and `cursor`
      // stay exactly as they were, so the reader keeps the list they had
      // (D-LD2). Emptying it here is what used to make a dropped connection
      // read as "nobody has asked for anything yet".
      if (page !== null) {
        setRequests(page.requests);
        setCursor(page.cursor);
      }
      setFailed(page === null);
      setLoading(false);
    });
  }, [q, sort, status, enabled]);

  useEffect(() => {
    run();
    return () => {
      generation.current += 1;
    };
  }, [run]);

  const loadMore = useCallback(() => {
    if (cursor === null) return;
    const mine = generation.current;
    setLoadingMore(true);
    void fetchBoardPage({ q, sort, status, cursor }).then((page) => {
      // A page that arrives after the query changed belongs to the old question.
      if (generation.current !== mine) return;
      if (page !== null) {
        setRequests((current) => [...current, ...page.requests]);
        setCursor(page.cursor);
      }
      setFailed(page === null);
      setLoadingMore(false);
    });
  }, [cursor, q, sort, status]);

  const toggleVote = useCallback(
    async (requestId: string): Promise<VoteResult> => {
      const before = requests.find((request) => request.id === requestId);
      if (before === undefined) return { ok: false, reason: 'not-found' };
      const voted = !before.votedByMe;

      // The guess, so the control answers the finger. `Math.max` because this is
      // the one place a count is computed rather than read, and a row that
      // briefly showed −1 would be worse than one that waited.
      setRequests((current) =>
        withVote(current, requestId, voted, Math.max(0, before.voteCount + (voted ? 1 : -1))),
      );

      const result = await setRequestVote(requestId, voted);
      // And the truth — or, on a refusal, exactly what was on the row before the
      // finger landed. Either way the row ends up showing something the server
      // said, which is the property the optimistic step borrows against.
      setRequests((current) =>
        result.ok
          ? withVote(current, requestId, result.voted, result.voteCount)
          : withVote(current, requestId, before.votedByMe, before.voteCount),
      );
      return result;
    },
    [requests],
  );

  return {
    requests,
    loading,
    loadingMore,
    failed,
    hasMore: cursor !== null,
    loadMore,
    refresh: run,
    toggleVote,
  };
}

// ---------------------------------------------------------------------------
// The caller's own requests
// ---------------------------------------------------------------------------

/**
 * The caller's own requests, hidden ones included. Empty covers every kind of no.
 *
 * The same shape {@link fetchBoardPage} takes and for the same reason: this list
 * sits above a board that has to render whatever else happens, so a signed-out
 * visitor, a lapsed session and an unreachable server all mean "no rows of your
 * own to show" rather than an error state on somebody else's page.
 */
export async function fetchOwnRequests(): Promise<readonly OwnRequest[]> {
  try {
    const response = await fetch(REQUESTS_BASE_PATH, { headers: JSON_HEADERS });
    if (!response.ok) return [];
    const body = (await response.json()) as Partial<OwnRequestsResponse>;
    return body.requests ?? [];
  } catch {
    return [];
  }
}

/** What {@link useOwnRequests} returns. */
export interface OwnRequestsState {
  readonly requests: readonly OwnRequest[];
  /** True until the first answer lands. The section renders nothing until then. */
  readonly loading: boolean;
  /** Re-read them. What a successful post calls, beside the board's own refresh. */
  readonly refresh: () => void;
}

/**
 * The "Yours" list.
 *
 * `enabled` is how a signed-out visitor's board makes no request at all — the
 * reason `useIngestTokens` and `useShareLinks` both have one, and it matters more
 * here than on either: the board is the one page in the product built to be read
 * with no account, and a 401 in its network panel on every visit would be a page
 * asking a question it already knows the answer to.
 *
 * A generation counter rather than a `cancelled` flag, matching {@link useBoard}
 * above: a refresh fired by a successful post can land after another one, and the
 * question is which answer is still the one being asked.
 */
/** What {@link useBoardCounts} returns. Zeroes until the first answer lands. */
export interface BoardCountsState {
  readonly counts: BoardCounts;
  /**
   * True until the first answer lands.
   *
   * The page needs this to tell "no shipped requests" from "we have not asked
   * yet": without it, a board whose open list comes back empty a moment before
   * the counts arrive flashes "nobody has asked for anything yet" at somebody
   * looking at a page with twelve shipped requests on it.
   */
  readonly loading: boolean;
  /** Re-read them. What a successful post calls, alongside the lists. */
  readonly refresh: () => void;
}

/** Every status at zero: what a heading reads before the first answer arrives. */
const NO_COUNTS: BoardCounts = { open: 0, planned: 0, shipped: 0, declined: 0 };

/**
 * How many requests stand at each status, for the section headings.
 *
 * A second hook rather than a field on {@link useBoard}, for the reason the
 * route is separate (`worker/board.ts`): the headings that need it most belong
 * to sections nobody has opened, and a count carried on a page of rows can only
 * describe a list somebody already asked for.
 *
 * It takes the search and not the sort. Ordering cannot change how many there
 * are, and a heading that re-fetched when somebody switched to "Newest" would
 * be spending a request to print the same number.
 *
 * A failure leaves the last good counts on screen and is not otherwise reported.
 * The lists say when the board is unreachable; a heading that fell back to
 * "Shipped (0)" would be a lie told twice on one page.
 */
export function useBoardCounts(q: string): BoardCountsState {
  const [counts, setCounts] = useState<BoardCounts>(NO_COUNTS);
  const [loading, setLoading] = useState(true);
  const generation = useRef(0);

  const refresh = useCallback(() => {
    generation.current += 1;
    const mine = generation.current;
    void fetchBoardCounts(q).then((next) => {
      if (generation.current !== mine) return;
      // A failure keeps the last good numbers and stops the waiting: the lists
      // below say when the board is unreachable, and a heading that fell back to
      // zero would be the same bad news told twice, in a place that cannot
      // explain itself.
      if (next !== null) setCounts(next);
      setLoading(false);
    });
  }, [q]);

  useEffect(() => {
    refresh();
    return () => {
      generation.current += 1;
    };
  }, [refresh]);

  return { counts, loading, refresh };
}

/** The counts, or `null` for every kind of "no". */
export async function fetchBoardCounts(q: string): Promise<BoardCounts | null> {
  try {
    const response = await fetch(boardCountsPath({ q }), { headers: JSON_HEADERS });
    if (!response.ok) return null;
    const body = (await response.json()) as Partial<BoardCountsResponse>;
    return body.counts ?? null;
  } catch {
    return null;
  }
}

export function useOwnRequests(enabled: boolean): OwnRequestsState {
  const [requests, setRequests] = useState<readonly OwnRequest[]>([]);
  const [loading, setLoading] = useState(enabled);
  const generation = useRef(0);

  const refresh = useCallback(() => {
    generation.current += 1;
    const mine = generation.current;
    setLoading(true);
    void fetchOwnRequests().then((next) => {
      if (generation.current !== mine) return;
      setRequests(next);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!enabled) {
      // Signing out empties the section rather than leaving the last account's
      // requests on screen under a board that has moved on.
      generation.current += 1;
      setRequests([]);
      setLoading(false);
      return;
    }
    refresh();
    return () => {
      generation.current += 1;
    };
  }, [enabled, refresh]);

  return { requests, loading, refresh };
}

/** One request's vote state, replaced. The list's order and length never move. */
function withVote(
  requests: readonly FeatureRequestCard[],
  requestId: string,
  votedByMe: boolean,
  voteCount: number,
): readonly FeatureRequestCard[] {
  return requests.map((request) =>
    request.id === requestId ? { ...request, votedByMe, voteCount } : request,
  );
}
